import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Sidebar, Nav } from '../../design-system-integration';
import { SkipToContent } from '../../accessibility';
import { useAuth } from '../../context/AuthContext';
import { useLayout } from '../../context/LayoutContext';
import { useLocation } from 'react-router-dom';
import { api } from '../../services/api';
import ChurchSwitcher from '../common/ChurchSwitcher';
import RoleSelectorDropdowns from '../common/RoleSelectorDropdowns';
import { branding, showLogoText, showLogo } from '../../config/branding';

/**
 * Fixed MainLayout Component
 * 
 * This version properly handles scrolling and layout without flickering
 */

const MOBILE_BREAKPOINT = '768px';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  position: relative;
`;

const SidebarContainer = styled.div<{ $isOpen: boolean }>`
  width: 216px;
  background-color: var(--background-secondary);
  border-right: 1px solid var(--border-light);
  position: sticky;
  top: 0;
  height: 100vh;
  overflow: hidden;
  transition: transform 0.3s ease-in-out;
  display: flex;
  flex-direction: column;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    position: fixed;
    left: 0;
    top: 0;
    z-index: 100;
    transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
    box-shadow: ${props => props.$isOpen ? '2px 0 10px rgba(0, 0, 0, 0.1)' : 'none'};
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
`;

const MobileOverlay = styled.div<{ $isOpen: boolean }>`
  display: none;
  
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 99;
  }
`;

const HamburgerButton = styled.button`
  display: none; /* Hidden by default on desktop */
  flex-direction: column;
  justify-content: space-around;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  width: 32px;
  height: 32px;
  margin-right: var(--spacing-sm);
  
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: flex; /* Show only on mobile */
  }
  
  span {
    display: block;
    width: 100%;
    height: 3px;
    background-color: var(--text-primary);
    border-radius: 2px;
    transition: all 0.3s ease-in-out;
  }
  
  &:hover span {
    background-color: var(--primary-500);
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0; /* Prevent flex child from growing beyond parent */
`;

const Header = styled.header`
  height: 48px;
  padding-bottom: 10px;
  box-sizing: content-box;
  border-bottom: 1px solid var(--border-light);
  background-color: var(--background-primary);
  position: sticky;
  top: 0;
  z-index: 10;
`;

const HeaderContent = styled.div<{ $contentInset: string; $mobileContentInset: string }>`
  height: 100%;
  padding: 0 ${props => props.$contentInset};
  padding-top: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 ${props => props.$mobileContentInset};
    padding-top: 5px;
  }
`;

const CloseButton = styled.button`
  display: none;
  position: absolute;
  top: 50%;
  right: 5px;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 24px;
  color: var(--text-primary);
  width: 32px;
  height: 32px;
  
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: block;
  }
  
  &:hover {
    color: var(--primary-500);
  }
`;

const MainContent = styled.main<{ $removePadding?: boolean }>`
  flex: 1;
  background-color: var(--background-tertiary);
  padding: ${props => props.$removePadding ? '0' : 'var(--spacing-lg)'};

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0;
  }
`;

const Footer = styled.footer`
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid var(--border-light);
  text-align: center;
  color: var(--text-tertiary);
  font-size: var(--font-size-sm);
  background-color: var(--background-primary);
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--primary-500);

  img {
    height: 32px;
    width: auto;
  }
`;

const NavContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex: 1;
  }
`;

const DesktopNav = styled(Nav)`
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`;

const PageTitle = styled.h1`
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0;
  margin-left: var(--spacing-md);
  
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: var(--font-size-xl);
    margin-left: var(--spacing-sm);
  }
`;

const SidebarDivider = styled.div`
  height: 1px;
  background-color: var(--border-medium);
  margin: var(--spacing-xs) var(--spacing-md);
`;

const SectionHeader = styled.div`
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const LogoutSection = styled.div`
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid var(--border-light);
  background-color: var(--background-primary);
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  width: 100%;
  border: none;
  background: transparent;
  color: var(--text-tertiary);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: var(--error-600);
  }

  &:focus {
    outline: 2px solid var(--primary-500);
    outline-offset: 2px;
  }
`;

const ChurchSelectorBar = styled.div<{ $contentInset: string; $mobileContentInset: string }>`
  background: var(--background-primary);
  border-bottom: 1px solid var(--border-light);
  padding: 0 ${props => props.$contentInset};
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 58px; /* Below header (48px + 10px padding) */
  z-index: 9;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 ${props => props.$mobileContentInset};
  }
`;

const TestModeBanner = styled.div<{ $contentInset: string; $mobileContentInset: string }>`
  background: var(--warning-100);
  color: var(--warning-700);
  padding: 8px ${props => props.$contentInset};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  border-bottom: 2px solid var(--warning-500);
  position: sticky;
  top: 96px; /* Below header and church selector */
  z-index: 8;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 8px ${props => props.$mobileContentInset};
  }

  .test-icon {
    font-size: var(--font-size-lg);
  }

  .test-label {
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .test-details {
    font-weight: var(--font-weight-normal);
  }
`;

interface MainLayoutFixedProps {
  children: React.ReactNode;
}

const MainLayoutFixed: React.FC<MainLayoutFixedProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userMinistries, setUserMinistries] = useState<any[]>([]);
  const { user, logout } = useAuth();
  const { contentInset, mobileContentInset } = useLayout();
  const location = useLocation();

  // Load user ministries for dropdown
  useEffect(() => {
    if (user) {
      loadUserMinistries();
    }
  }, [user]);

  const loadUserMinistries = async () => {
    try {
      // Use unifiedApi which properly handles authentication
      const { unifiedApi } = await import('../../services/UnifiedApiService');
      const response = await unifiedApi.get('/churches/user-ministries/');
      setUserMinistries(response);
    } catch (error) {
      // Silently fail for authentication errors - user is likely not logged in yet
      if (!error.message?.includes('No authentication token available')) {
        console.error('Failed to load user ministries:', error);
      }
    }
  };

  // Build dashboard menu with ministry dropdown if applicable
  const buildDashboardMenu = () => {
    const baseItem = { id: 'dashboard', label: 'Impact Dashboard', href: '/dashboard', icon: '📊' };
    
    // If user has ministry access, create dropdown
    if (userMinistries.length > 0) {
      return {
        ...baseItem,
        submenu: [
          { id: 'church-dashboard', label: 'Church Overview', href: '/dashboard' },
          ...userMinistries.map(m => ({
            id: `ministry-${m.id}`,
            label: `${m.name} Dashboard`,
            href: `/ministry/${m.id}/dashboard`,
            indent: m.level
          }))
        ]
      };
    }
    
    return baseItem;
  };

  // Group menu items logically based on user interaction patterns
  const dashboardGroup = [
    buildDashboardMenu(),
  ];
  
  // Read-only Analysis (Information & Insights)
  const analysisGroup = [
    { id: 'analytics', label: 'Analytics', href: '/analytics', icon: '📈', description: 'Metrics & trends' },
    { id: 'attendance', label: 'Attendance', href: '/attendance', icon: '✅', description: 'Track participation' },
    { id: 'engagement', label: 'Engagement', href: '/engagement', icon: '🤝', description: 'Member activity' },
  ];

  // Scheduling (Calendar & Reservations)
  const schedulingGroup = [
    { id: 'calendar', label: 'Calendar', href: '/calendar', icon: '📅', description: 'Events & meetings' },
    { id: 'reservations', label: 'Reservations', href: '/reservations', icon: '🚪', description: 'Room bookings' },
  ];
  
  // User Input/Direction (Active Participation)
  const inputGroup = [
    { id: 'assessments', label: 'Assessments', href: '/assessments', icon: '📝' },
    { id: 'gamification', label: 'Gamification', href: '/gamification', icon: '🎮' },
  ];
  
  // Planning and Strategy (Hierarchical Flow)
  const planningGroup = [
    { id: 'strategic-planning', label: 'Strategic Planning', href: '/strategic-planning', icon: '📐' },
    { id: 'learning-paths', label: 'Learning Paths', href: '/learning', icon: '🎓' },
    { id: 'action-plans', label: 'Action Plans', href: '/action-plans', icon: '📋' },
    { id: 'priorities', label: 'Priorities', href: '/priorities', icon: '🎯' },
  ];
  
  // Resources and Insights
  const resourcesGroup = [
    { id: 'knowledge', label: 'Knowledge Base', href: '/knowledge', icon: '📚' },
  ];

  // Check if user has admin/pastor role
  const fixedRole = user?.role?.toLowerCase();
  const hasAdminOrPastorRole = fixedRole === 'admin' || fixedRole === 'administrator' || fixedRole === 'pastor' || user?.is_superuser ||
    user?.church_roles?.some((role: any) =>
      ['admin', 'pastor', 'executive_pastor', 'church_admin'].includes(role.role?.toLowerCase())
    );

  // Check for audit access (pastor, admin, prayer ministry manager, denomination admin)
  const hasAuditAccess = user?.is_superuser ||
    fixedRole === 'admin' ||
    fixedRole === 'administrator' ||
    fixedRole === 'pastor' ||
    fixedRole === 'prayer_ministry_manager' ||
    fixedRole === 'denomination_admin' ||
    user?.church_roles?.some((role: any) =>
      ['admin', 'pastor', 'executive_pastor', 'prayer_ministry_manager'].includes(role.role?.toLowerCase())
    );

  // Ministry - Prayer Team, etc. (Prayer Audit shown only to authorized users)
  const ministryGroup = [
    { id: 'prayer-requests', label: 'Prayer Requests', href: '/prayer-requests', icon: '🙏', description: 'Prayer inbox' },
    ...(hasAuditAccess
      ? [{ id: 'prayer-audit', label: 'Prayer Audit', href: '/admin/prayer-audit', icon: '🔍', description: 'QA & Investigation' }]
      : []),
  ];

  // Settings available to church admins/pastors
  const settingsGroup = hasAdminOrPastorRole
    ? [
        { id: 'profile', label: 'Church Profile', href: '/church', icon: '🏢' },
        ...(fixedRole === 'admin' || fixedRole === 'administrator' || fixedRole === 'superadmin' || user?.is_superuser
          ? [{ id: 'settings', label: 'Settings', href: '/settings', icon: '⚙️' }]
          : []),
      ]
    : [];

  // Platform Settings - SuperUser only
  const platformSettingsGroup = user?.is_superuser
    ? [
        { id: 'platform-settings', label: 'Platform Settings', href: '/platform-settings', icon: '🔐' }
      ]
    : [];
  
  // Combine all items for finding current page
  const allItems = [...dashboardGroup, ...analysisGroup, ...schedulingGroup, ...inputGroup, ...planningGroup, ...resourcesGroup, ...ministryGroup, ...settingsGroup, ...platformSettingsGroup];

  // Determine the current page title based on the URL
  const getCurrentPageTitle = () => {
    const pathname = location.pathname;

    // Custom title for metric detail pages - extract from document.title
    if (pathname.startsWith('/metrics/')) {
      // Extract metric name from document title (format: "Metric Name - ConnectPath")
      const titleParts = document.title.split(' - ');
      return titleParts[0] || 'Metric Details';
    }

    // Custom title for engagement page
    if (pathname.startsWith('/engagement')) {
      return 'Engagement Dashboard';
    }

    const currentItem = allItems.find(item => pathname.startsWith(item.href));
    return currentItem?.label || 'Dashboard';
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <LayoutContainer>
      <SkipToContent />
      <MobileOverlay $isOpen={isSidebarOpen} onClick={closeSidebar} />
      <SidebarContainer $isOpen={isSidebarOpen} role="navigation" aria-label="Main Navigation">
        <div style={{ padding: '16px 16px 16px 28px', textAlign: 'left', position: 'relative' }}>
          <Logo>{showLogo() && <img src={branding.logo} alt={branding.logoWithText ? branding.logoAlt : ''} />}{showLogoText() && branding.appName}</Logo>
          <CloseButton onClick={closeSidebar} aria-label="Close menu">
            ✕
          </CloseButton>
        </div>

        <SidebarContent>
          <Sidebar collapsible={false}>
            {/* Dashboard - simplified without dropdown for now */}
            <Sidebar.Item
              icon="📊"
              href="/dashboard"
              label="Impact Dashboard"
              onClick={closeSidebar}
            />

            <SidebarDivider />

            {/* Analysis & Insights - Read-only information */}
            <SectionHeader>Analysis & Insights</SectionHeader>
            {analysisGroup.map((item) => (
              <Sidebar.Item
                key={item.id}
                icon={item.icon}
                href={item.href}
                label={item.label}
                description={item.description}
                onClick={closeSidebar}
              />
            ))}

            <SidebarDivider />

            {/* Scheduling - Calendar & Room Reservations */}
            <SectionHeader>Scheduling</SectionHeader>
            {schedulingGroup.map((item) => (
              <Sidebar.Item
                key={item.id}
                icon={item.icon}
                href={item.href}
                label={item.label}
                description={item.description}
                onClick={closeSidebar}
              />
            ))}

            <SidebarDivider />

            {/* Activities & Input - User participation */}
            <SectionHeader>Activities & Input</SectionHeader>
            {inputGroup.map((item) => (
              <Sidebar.Item
                key={item.id}
                icon={item.icon}
                href={item.href}
                label={item.label}
                onClick={closeSidebar}
              />
            ))}

            <SidebarDivider />

            {/* Planning & Strategy - Hierarchical flow */}
            <SectionHeader>Planning & Strategy</SectionHeader>
            {planningGroup.map((item) => (
              <Sidebar.Item
                key={item.id}
                icon={item.icon}
                href={item.href}
                label={item.label}
                onClick={closeSidebar}
              />
            ))}

            <SidebarDivider />

            {/* Resources & Insights */}
            <SectionHeader>Resources & Insights</SectionHeader>
            {resourcesGroup.map((item) => (
              <Sidebar.Item
                key={item.id}
                icon={item.icon}
                href={item.href}
                label={item.label}
                onClick={closeSidebar}
              />
            ))}

            <SidebarDivider />

            {/* Ministry */}
            <SectionHeader>Ministry</SectionHeader>
            {ministryGroup.map((item) => (
              <Sidebar.Item
                key={item.id}
                icon={item.icon}
                href={item.href}
                label={item.label}
                description={item.description}
                onClick={closeSidebar}
              />
            ))}

            {/* Settings Section - Church Profile always visible, Settings for admins */}
            {settingsGroup.length > 0 && (
              <>
                <SidebarDivider />
                <SectionHeader>Settings</SectionHeader>
                {settingsGroup.map((item) => (
                  <Sidebar.Item
                    key={item.id}
                    icon={item.icon}
                    href={item.href}
                    label={item.label}
                    onClick={closeSidebar}
                  />
                ))}
              </>
            )}

            {/* Platform Settings - SuperUser only */}
            {platformSettingsGroup.length > 0 && (
              <>
                <SidebarDivider />
                <SectionHeader>SuperUser</SectionHeader>
                {platformSettingsGroup.map((item) => (
                  <Sidebar.Item
                    key={item.id}
                    icon={item.icon}
                    href={item.href}
                    label={item.label}
                    onClick={closeSidebar}
                  />
                ))}
              </>
            )}
          </Sidebar>
        </SidebarContent>

        {/* Logout - Always at bottom */}
        <LogoutSection>
          <LogoutButton onClick={() => { closeSidebar(); logout(); }} aria-label="Log out">
            <span>🚪</span>
            <span>Log Out</span>
          </LogoutButton>
        </LogoutSection>
      </SidebarContainer>
      
      <ContentWrapper>
        <Header role="banner">
          <HeaderContent $contentInset={contentInset} $mobileContentInset={mobileContentInset}>
            <NavContainer>
              <HamburgerButton onClick={toggleSidebar} aria-label="Open menu">
                <span></span>
                <span></span>
                <span></span>
              </HamburgerButton>
              <PageTitle>{getCurrentPageTitle()}</PageTitle>
            </NavContainer>
            <div>
              <a
                href="/user-profile"
                style={{
                  textDecoration: 'none',
                  color: 'var(--primary-500)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-xs)'
                }}
              >
                <span>{user?.name || 'User Profile'}</span>
              </a>
            </div>
          </HeaderContent>
        </Header>
        
        {/* Role and Church Selector - Below header for users with multiple roles or superusers */}
        {user && <ChurchSelectorBar $contentInset={contentInset} $mobileContentInset={mobileContentInset}>
          <RoleSelectorDropdowns showChurchSelector={true} />
        </ChurchSelectorBar>}

        {/* Test Mode Banner - Show when test account is active */}
        {user?.test_account && (
          <TestModeBanner $contentInset={contentInset} $mobileContentInset={mobileContentInset}>
            <span className="test-icon">⚠️</span>
            <span className="test-label">Test Mode Active:</span>
            <span className="test-details">
              {user.test_account.name} ({user.test_account.role_type})
              {user.test_account.church && ` - ${user.test_account.church.name}`}
            </span>
          </TestModeBanner>
        )}

        <MainContent id="main-content" $removePadding={location.pathname.startsWith('/analytics')}>
          {children}
        </MainContent>
        
        <Footer role="contentinfo">
          &copy; {new Date().getFullYear()} ConnectPath. All rights reserved.
        </Footer>
      </ContentWrapper>
    </LayoutContainer>
  );
};

export default MainLayoutFixed;